import { storage } from "./Firebase";
import Toast from "./Toast";

class NotesGenerator {
  context: AudioContext;

  constructor(context: AudioContext) {
    this.context = context;
  }

  /**
   * takes an object that maps his keys to probabilities (or counts) and normalize each probability
   */
  normalizedObj(obj: { [key: string]: number }) {
    const res: { [key: string]: number } = {};
    let sum = 0;
    for (let k in obj) {
      sum += obj[k];
    }
    for (let k in obj) {
      res[k] = obj[k] / sum;
    }
    return res;
  }

  /**
   * takes an object that maps his keys to probabilities (or counts)
   * returns a function that returns one of the keys with that discrete distribution
   */
  distribution(obj: { [key: string]: number }) {
    const o = this.normalizedObj(obj);
    return function () {
      let count = 0;
      const rand = Math.random();

      for (let key in o) {
        count += o[key];

        if (rand < count) return key;
      }
    };
  }

  getRandomInt(min: number, max: number) {
    return Math.round(Math.random() * (max - min) + min);
  }

  // The factor determines the octave
  // Choose the importance of each factor
  nextFac() {
    return this.distribution({ "-1": 2, "0": 2, "1": 2, "2": 1, "3": 0 });
  }

  // Choose the importance of each note. Notes with a 0 probability won't be played
  nextNote() {
    return this.distribution({
      131: 2,
      139: 0,
      147: 1,
      156: 0,
      165: 2,
      175: 1,
      185: 0,
      196: 2,
      208: 0,
      220: 1,
      233: 0,
      247: 1,
      262: 2,
      277: 0,
      294: 1,
      311: 0,
      330: 2,
      349: 1,
      370: 0,
      392: 2,
      415: 0,
      440: 1,
      466: 0,
      496: 1,
    });
  }

  playNote(maxFactor: number) {
    // Create osciallator
    const oscillator = this.context.createOscillator();
    const waveForm = Math.random() > 0.5 ? "sine" : "triangle";
    oscillator.type = waveForm;
    // Update fac
    const nextFacDistribution = this.nextFac();
    let fac = parseInt(nextFacDistribution() as string);
    while (fac > maxFactor) {
      fac = parseInt(nextFacDistribution() as string);
    }
    console.log(`fac ${fac}`);
    oscillator.frequency.value =
      parseInt(nextFacDistribution() as string) * Math.pow(2, fac);
    // Create gain node
    const gainNode = this.context.createGain();
    gainNode.gain.value = 10;
    // Generate sound
    oscillator.connect(gainNode);
    gainNode.connect(this.context.destination);
    oscillator.start(this.context.currentTime);
    const duration = this.getRandomInt(2, 6);
    gainNode.gain.linearRampToValueAtTime(
      0.0001,
      this.context.currentTime + duration
    );
    oscillator.stop(this.context.currentTime + duration);
  }
}

const TuneGenerator = (context: AudioContext): number[] => {
  const frequencies = [240, 270, 440, 840];
  const intervals = [2400, 2600, 6200, 9200];
  const sound = (frequency: number) => {
    var o = context.createOscillator();
    var g = context.createGain();
    o.connect(g);
    g.connect(context.destination);
    o.frequency.value = frequency;
    o.start(context.currentTime);
  };

  setInterval(function () {
    sound(frequencies[0]);
  }, intervals[0]);
  setInterval(function () {
    sound(frequencies[1]);
  }, intervals[1]);

  setInterval(function () {
    sound(frequencies[2]);
  }, intervals[2]);

  setInterval(function () {
    sound(frequencies[3]);
  }, intervals[3]);

  return frequencies;
};

const Noise = (context: AudioContext): Float32Array => {
  const noiseDuration = 10;
  const bufferSize = context.sampleRate * noiseDuration; // set the time of the note
  const buffer = context.createBuffer(1, bufferSize, context.sampleRate); // create an empty buffer
  let data = buffer.getChannelData(0); // get data
  const bandHz = 1000;
  // fill the buffer with noise
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  // create a buffer source for our created data
  let noise = context.createBufferSource();
  noise.buffer = buffer;

  let bandpass = context.createBiquadFilter();
  bandpass.type = "lowpass";
  bandpass.frequency.value = bandHz;

  // connect our graph
  noise.connect(bandpass).connect(context.destination);
  noise.start(context.currentTime);
  return data;
};

const addAudioBufferFromUrl = (
  musicService: MusicService,
  name: string,
  url: string
) => {
  const request = new XMLHttpRequest();
  request.open("GET", url, true);
  request.responseType = "arraybuffer";
  request.onload = function () {
    musicService.ctx.decodeAudioData(
      request.response,
      function (buffer) {
        musicService.addSourceBuffer(buffer, name);
      },
      function () {
        console.log("Decoding error! ");
      }
    );
  };
  request.send();
};

const JanuarySong = async (musicService: MusicService): Promise<boolean> => {
  await storage
    .ref("/audio/JanuarySongFinal.mp3")
    .getDownloadURL()
    .then((value) => {
      addAudioBufferFromUrl(musicService, "January", value);
    });
  return true;
};

const DhinchakSong = async (musicService: MusicService): Promise<boolean> => {
  await storage
    .ref("/audio/dhinchak.mp3")
    .getDownloadURL()
    .then((value) => {
      addAudioBufferFromUrl(musicService, "Dhinchak", value);
    });
  return true;
};

const CricketSong = async (musicService: MusicService): Promise<boolean> => {
  await storage
    .ref("/audio/night-ambient-sounds-cricket.mp3")
    .getDownloadURL()
    .then((value) => {
      addAudioBufferFromUrl(musicService, "Cricket", value);
    });
  return true;
};

const BattleSong = async (musicService: MusicService): Promise<boolean> => {
  await storage
    .ref("/audio/epic_battle_music.mp3")
    .getDownloadURL()
    .then((value) => {
      addAudioBufferFromUrl(musicService, "Battle", value);
    });
  return true;
};

class MusicService {
  ctx: AudioContext;
  name: string;
  dataArray: Uint8Array | undefined;
  analyzer: AnalyserNode;
  private sourceBufferMap: { [key: string]: AudioBuffer };
  private sourceMap: { [key: string]: AudioBufferSourceNode };
  currentlyPlaying: string | undefined = undefined;

  constructor(ctx: AudioContext, name: string) {
    this.ctx = ctx;
    this.name = name;
    this.analyzer = ctx.createAnalyser();
    this.analyzer.fftSize = 2048;
    this.analyzer.connect(ctx.destination);
    this.sourceBufferMap = {};
    this.sourceMap = {}
  }

  addSourceBuffer(buffer: AudioBuffer, name: string) {
    this.sourceBufferMap[name] = buffer;
  }

  startSource(name: string) {
    if(!(name in this.sourceBufferMap)) return
    const source = new AudioBufferSourceNode(this.ctx)
    this.sourceMap[name] = source
    source.buffer = this.sourceBufferMap[name]
    source.loop = true
    source.connect(this.analyzer);
    source.start(this.ctx.currentTime)
    
  }

  setDataArray(arr: Uint8Array): boolean {
    this.dataArray = arr;
    return true;
  }

  startBgMusic(name: string): boolean {
    if (name in this.sourceBufferMap && this.currentlyPlaying != name) {
      if (typeof this.currentlyPlaying != "undefined") {
        this.sourceMap[this.currentlyPlaying].stop()
        delete this.sourceMap[this.currentlyPlaying]
      }
      this.currentlyPlaying = name;
      console.log(`Playing ${name}`)
      this.startSource(name)
    }
    return true;
  }
}

const getMusic = async () => {
  const context = new AudioContext();
  // const frequencies = TuneGenerator(context)
  // const data = Noise(context)
  const musicService: MusicService = new MusicService(context, "SoftMusic");
  await JanuarySong(musicService)
  await DhinchakSong(musicService)
  await CricketSong(musicService)
  const bufferLength = musicService.analyzer.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);
  musicService.setDataArray(dataArray);
  Toast("🎶");
  return musicService;
};

export { TuneGenerator, Noise, JanuarySong, getMusic, MusicService };
